import type {
  StockBalance,
  StockCommand,
  StockCommandResult,
  StockItem,
  StockItemInput,
  StockMovementPage,
  WarehouseLocation,
  WarehouseLocationInput,
} from '../domain/stock';
import { stockApi as liveStockApi } from './live-stock-api';
import { stockApi as mockStockApi } from './mock-fulfillment-api';

export interface StockBalanceFilters {
  stockItemId?: string | undefined;
  locationId?: string | undefined;
  siteId?: string | undefined;
}

export interface StockMovementFilters {
  stockItemId?: string | undefined;
  locationId?: string | undefined;
}

/**
 * Stock catalog, locations and the movement ledger, backed by leatherback's
 * StockService (tidaltech/koi#8096). The mock shares its ledger with the
 * pick-pack mock so received stock can be picked.
 */
export interface StockApi {
  listStockItems(includeInactive?: boolean): Promise<StockItem[]>;
  createStockItem(input: StockItemInput): Promise<StockItem>;
  updateStockItem(id: string, version: number, input: StockItemInput): Promise<StockItem>;
  /** Sets the item's photo (JPEG, PNG or WebP, at most 3 MB), replacing any earlier one. */
  setStockItemImage(id: string, image: Blob): Promise<StockItem>;
  deleteStockItemImage(id: string): Promise<StockItem>;
  /** Where an <img> loads the item's current photo; undefined when it has none. */
  stockItemImageUrl(item: StockItem): string | undefined;
  listWarehouseLocations(siteId?: string, includeInactive?: boolean): Promise<WarehouseLocation[]>;
  createWarehouseLocation(input: WarehouseLocationInput): Promise<WarehouseLocation>;
  updateWarehouseLocation(
    id: string,
    version: number,
    input: Pick<WarehouseLocationInput, 'code' | 'displayName' | 'type' | 'active'>,
  ): Promise<WarehouseLocation>;
  listStockBalances(filters?: StockBalanceFilters): Promise<StockBalance[]>;
  listStockMovements(
    filters?: StockMovementFilters,
    pageToken?: string,
  ): Promise<StockMovementPage>;
  recordStockCommand(command: StockCommand): Promise<StockCommandResult>;
}

export const stockApi: StockApi =
  import.meta.env.VITE_API_MODE === 'live' ? liveStockApi : mockStockApi;
